// ** Modules
import Head from 'next/head';
import React from 'react';
import { getProviders, signIn, useSession } from 'next-auth/react';

// ** Components
import LoginOverlay from '@/components/common/LoginOverlay';

// ** Layout
import VerticalLayout from './vertical/VerticalLayout';
import HorizontalLayout from './horizontal/HorizontalLayout';

// ** Types
import type { LayoutProps } from '@/types/layoutProps';
import type { LoginOverlayAction } from '@/components/common/LoginOverlay';

const WELCOME_STORAGE_KEY = 'tischlerWelcomeCompleted';

const Layout = ({
	hidden,
	children,
	settings,
	saveSettings,
	currentUser,
	locale,
	host,
	metadata,
	verticalLayoutProps,
	horizontalLayoutProps,
	footerProps,
	providers
}: LayoutProps) => {
	// Hooks
	const isCollapsed = React.useRef(settings.navcollapsed);

	// Welcome / login overlay: shown only once per browser (tracked via
	// localStorage, which persists across tabs and sessions) and never to
	// already logged-in users. Lives here (rather than inside Horizontal/
	// VerticalLayout) so it renders on every viewport. Reading localStorage is
	// deferred to an effect so the server render and first client render stay
	// in sync (no hydration mismatch).
	//
	// The decision is keyed off the resolved NextAuth `status` string rather
	// than the `currentUser` object: the session is refetched on an interval
	// and on every window focus (see SessionProvider in _app.tsx), which mints
	// a new `currentUser` reference each time. Depending on that reference made
	// the effect re-run constantly and could flash the overlay open before auth
	// resolved. `status` is a stable string, so we only open once we positively
	// know the visitor is unauthenticated, and never while still `loading`.
	const { status: sessionStatus } = useSession();
	const [isLoginOverlayOpen, setIsLoginOverlayOpen] = React.useState(false);

	React.useEffect(() => {
		if (sessionStatus === 'loading') return;

		const completed = window.localStorage.getItem(WELCOME_STORAGE_KEY) === 'true';

		const allowedHosts = (
			process.env.NEXT_PUBLIC_ALLOWED_HOSTS || 'beta.tischler.studio,tischler.nrw,localhost:3000'
		).split(',');
		const isAllowedHost = allowedHosts.some((allowed) => {
			if (!host) return false;
			return host.toLowerCase().trim() === allowed.toLowerCase().trim();
		});

		setIsLoginOverlayOpen(sessionStatus === 'unauthenticated' && !completed && isAllowedHost);
	}, [sessionStatus, host]);

	const completeWelcome = React.useCallback(() => {
		window.localStorage.setItem(WELCOME_STORAGE_KEY, 'true');
		setIsLoginOverlayOpen(false);
	}, []);

	const handleLogin = React.useCallback(async () => {
		try {
			completeWelcome();
			const availableProviders = await getProviders();
			const first = availableProviders ? Object.values(availableProviders)[0] : undefined;
			if (first) {
				await signIn(first.id, { callbackUrl: window.location.href });
			} else {
				console.warn('No authentication providers available');
			}
		} catch (error) {
			console.error('Login failed:', error);
		}
	}, [completeWelcome]);

	const loginOverlayActions = React.useMemo<LoginOverlayAction[]>(
		() => [
			{
				id: 'member-login',
				label: 'Mitglieder Login',
				description: 'Exklusiver Zugriff auf Ihr Dashboard & Fachdokumente.',
				onClick: handleLogin
			},
			{
				id: 'guest-continue',
				label: 'Als Gast fortfahren',
				description: 'Website entdecken, News & Infos lesen.',
				onClick: completeWelcome
			}
		],
		[handleLogin, completeWelcome]
	);

	React.useEffect(() => {
		const fallbackLayout = settings.lastLayout || 'default';

		if (hidden) {
			if (settings.navcollapsed) {
				saveSettings({
					...settings,
					navcollapsed: false,
					layout: 'verticalNavigation'
				});
				isCollapsed.current = true;
			}
		} else {
			if (isCollapsed.current) {
				saveSettings({
					...settings,
					navcollapsed: true,
					layout: fallbackLayout
				});
				isCollapsed.current = false;
			} else {
				if (settings.lastLayout !== settings.layout) {
					saveSettings({ ...settings, layout: fallbackLayout });
				}
			}
		}
	}, [hidden, saveSettings, settings]);

	return (
		<React.Fragment>
			{/* Title of the page */}
			<Head>
				<title>{metadata?.title}</title>
			</Head>

			<LoginOverlay open={isLoginOverlayOpen} onClose={completeWelcome} actions={loginOverlayActions} />

			{settings.layout === 'default' && horizontalLayoutProps ? (
				<HorizontalLayout
					providers={providers}
					currentUser={currentUser}
					locale={locale}
					host={host}
					hidden={hidden}
					settings={settings}
					saveSettings={saveSettings}
					horizontalLayoutProps={horizontalLayoutProps}
					footerProps={footerProps}
				>
					{children}
				</HorizontalLayout>
			) : (
				verticalLayoutProps && (
					<VerticalLayout
						providers={providers}
						currentUser={currentUser}
						locale={locale}
						host={host}
						hidden={hidden}
						settings={settings}
						saveSettings={saveSettings}
						verticalLayoutProps={verticalLayoutProps}
						footerProps={footerProps}
					>
						{children}
					</VerticalLayout>
				)
			)}
		</React.Fragment>
	);
};

export default Layout;
